import React from "react";
import { CustomModal } from "components/RFQ/Modal/CustomModal";
import { Button } from "newui/components/Button";
import { FiAlertCircle, FiCheckCircle } from "react-icons/fi";
import type { ImportClarificationsResult } from "../../types/clarification.type";

interface ImportStatusModalProps {
  isOpen: boolean;
  result: ImportClarificationsResult | null;
  onClose: () => void;
}

export const ImportStatusModal: React.FC<ImportStatusModalProps> = ({
  isOpen,
  onClose,
  result,
}) => {
  if (!result) {
    return null;
  }

  const hasErrors = result.errors.length > 0;
  const headerConfig = hasErrors
    ? {
        icon: "alert-triangle",
        bgColor: "bg-utility-error-50",
        iconColor: "text-utility-error-500",
        title: "Import Completed with Errors",
        description: `${result.imported_count} clarifications imported, ${result.errors.length} errors found.`,
      }
    : {
        icon: "check-circle",
        bgColor: "bg-green-50",
        iconColor: "text-green-600",
        title: "Import Successful",
        description: `Successfully imported ${result.imported_count} clarifications.`,
      };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      header={headerConfig}
      size="xl"
      footerComponent={
        <div className="flex w-full justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Summary */}
        <div className="flex items-center gap-4 rounded-lg bg-gray-50 p-4">
          <div className="flex items-center gap-2">
            <FiCheckCircle className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium text-gray-700">
              {result.imported_count} imported
            </span>
          </div>
          {hasErrors && (
            <div className="flex items-center gap-2">
              <FiAlertCircle className="h-5 w-5 text-red-600" />
              <span className="text-sm font-medium text-gray-700">
                {result.errors.length} errors
              </span>
            </div>
          )}
        </div>

        {/* Error Details */}
        {hasErrors && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-900">Error Details</h4>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                      Row
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                      Column
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                      Error
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {result.errors.map((error, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                        {error.row}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-500">
                        {error.column}
                      </td>
                      <td className="px-4 py-2 text-sm text-red-600">
                        {error.message}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  );
};
